import { Match } from "effect";
import type { AuditEvent } from "./audit.ts";
import type { AnnotationId } from "./ids.ts";
import type { Disposition } from "./review.ts";

// The one place that decides whether an `AuditEvent` is a terminal decision for an annotation, and
// which one (CON-738). `projectOpenFindings`, review-state's live fold (`applyEvent`), its
// cross-process double-decision guard (`terminalTarget`), the diagnostics rail
// (`projectDiagnostics`), and judge-reconcile (`judgeReconcileSets`) each used to carry their own list
// of "terminal" tags, kept in sync only by a comment ("the terminal set MUST match ... exactly").
// They now all ask this instead, so a new terminal event tag is a compile error here — via
// `Match.exhaustive`, not a hand-maintained tag set — rather than a silently-missed update in
// whichever of the five lists the author didn't remember.
//
// The outcome is a classification, not a verdict: each site decides for itself what a given outcome
// means for its own view. `kept` empties review-state's live queue via a status flip, but empties
// open-findings' backlog and the diagnostics rail via removal — same fact about the event, different
// consequence for the projection asking. `reclassified` and `disposed` carry the extra payload
// (`rule`/`rationale`, `disposition`) the one site that branches on it (review-state's live fold)
// needs, so that site never re-matches the raw event to get it.
export type AnnotationTerminalOutcome =
  | { readonly _tag: "not-terminal" }
  | { readonly _tag: "kept"; readonly annotationId: AnnotationId }
  | {
      readonly _tag: "reclassified";
      readonly annotationId: AnnotationId;
      readonly rule: string;
      readonly rationale: string;
    }
  | {
      readonly _tag: "disposed";
      readonly annotationId: AnnotationId;
      readonly disposition: Disposition;
    }
  | { readonly _tag: "ignored"; readonly annotationId: AnnotationId }
  | { readonly _tag: "removed"; readonly annotationId: AnnotationId };

export const classifyAnnotationTerminal = (event: AuditEvent): AnnotationTerminalOutcome =>
  Match.value(event).pipe(
    Match.tag("annotation.agreed", "annotation.agreed.v2", (e) => ({
      _tag: "kept" as const,
      annotationId: e.annotationId,
    })),
    Match.tag("annotation.reclassified", (e) => ({
      _tag: "reclassified" as const,
      annotationId: e.annotationId,
      rule: e.rule,
      rationale: e.rationale,
    })),
    Match.tag("triage.disposed", (e) => ({
      _tag: "disposed" as const,
      annotationId: e.annotationId,
      disposition: e.disposition,
    })),
    Match.tag("annotation.ignored.v2", (e) => ({
      _tag: "ignored" as const,
      annotationId: e.annotationId,
    })),
    Match.tag(
      "annotation.disagreed",
      "annotation.disagreed.v2",
      "annotation.closed",
      "annotation.closed.v2",
      "annotation.stripped",
      "annotation.stripped.v2",
      "annotation.retired",
      "annotation.resolved.v2",
      "annotation.retracted.v2",
      (e) => ({ _tag: "removed" as const, annotationId: e.annotationId }),
    ),
    Match.tag(
      "document.created",
      "annotation.created",
      "suggestion.created",
      "suggestion.accepted",
      "suggestion.rejected",
      "suggestion.amended",
      "edit.proposed",
      "edit.applied",
      "edit.discarded",
      "document.section-moved",
      "document.reverted",
      "document.archived",
      "document.unarchived",
      "document.tags-updated",
      "document.status-updated",
      "document.role-updated",
      "document.phase-advanced",
      "document.published",
      "document.rework-opened",
      "document.rework-closed",
      "draft.landed",
      "draft.rejected",
      "draft.discarded",
      "document.created.v2",
      "commit.landed",
      "document.generation-completed",
      "document.generation-failed",
      "annotation.created.v2",
      // U4's false-positive record is written ALONGSIDE `annotation.ignored.v2`, never instead of it:
      // the ignore is the terminal decision, this is only the corpus entry feeding the checker's
      // negative examples. Classifying it terminal would double-count the same decision.
      "annotation.falsePositive.v1",
      "migrated-from",
      () => ({ _tag: "not-terminal" as const }),
    ),
    Match.exhaustive,
  );
